import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare, Download, Filter, Search, Clock, CheckCircle,
  AlertCircle, XCircle, TrendingUp, Users, FileText, RefreshCw,
  ChevronDown, ChevronRight, Mail, User, Calendar, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors and icons
const statusConfig = {
  'new': { color: 'bg-blue-500', textColor: 'text-blue-400', icon: AlertCircle, label: 'New' },
  'in-progress': { color: 'bg-amber-500', textColor: 'text-amber-400', icon: Clock, label: 'In Progress' },
  'resolved': { color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle, label: 'Resolved' },
  'closed': { color: 'bg-slate-500', textColor: 'text-slate-400', icon: XCircle, label: 'Closed' }
};

// Feedback type labels
const feedbackTypeLabels = {
  'new-subject': 'New Subject Request',
  'new-topic': 'New Topic Request',
  'more-questions': 'More Questions',
  'bug-report': 'Bug Report',
  'feature-request': 'Feature Request',
  'content-quality': 'Content Quality',
  'general': 'General Feedback'
};

const AdminFeedback = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [total, setTotal] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchFeedback();
    fetchAnalytics();
  }, [statusFilter, typeFilter, userTypeFilter]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('feedbackType', typeFilter);
      if (userTypeFilter !== 'all') params.append('userType', userTypeFilter);
      
      const response = await axios.get(`${API}/api/admin/feedback?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.feedback || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/feedback/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/feedback/${feedbackId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFeedback();
      fetchAnalytics();
      setSelectedFeedback(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('feedbackType', typeFilter);
      
      const response = await axios.get(`${API}/api/admin/feedback/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export feedback');
    } finally {
      setExporting(false);
    }
  };

  // Filter feedback by search query
  const filteredFeedback = feedback.filter(fb => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      fb.name?.toLowerCase().includes(query) ||
      fb.email?.toLowerCase().includes(query) ||
      fb.message?.toLowerCase().includes(query) ||
      fb.subject?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/tutors')}
                className="text-slate-400 hover:text-white"
              >
                ← Back to Admin
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-violet-400" />
                  Feedback Management
                </h1>
                <p className="text-sm text-slate-400">View and manage user feedback</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={exportToExcel}
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {exporting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export to Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Feedback</p>
                    <p className="text-2xl font-bold text-white">{analytics.total}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-violet-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">New (Last 7 days)</p>
                    <p className="text-2xl font-bold text-blue-400">{analytics.recentCount}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Pending Review</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {(analytics.byStatus?.new || 0) + (analytics.byStatus?.['in-progress'] || 0)}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Resolved</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {analytics.byStatus?.resolved || 0}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Breakdown */}
        {analytics && (
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {/* By Type */}
            <Card className="bg-slate-800/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Tag className="w-4 h-4 text-violet-400" />
                  By Feedback Type
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(analytics.byType || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm">
                      {feedbackTypeLabels[type] || type}
                    </span>
                    <Badge className="bg-violet-500/20 text-violet-400">{count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* By User Type */}
            <Card className="bg-slate-800/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-400" />
                  By User Type
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(analytics.byUserType || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm capitalize">{type}</span>
                    <Badge className="bg-emerald-500/20 text-emerald-400">{count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* By Status */}
            <Card className="bg-slate-800/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  By Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(analytics.byStatus || {}).map(([status, count]) => {
                  const config = statusConfig[status] || statusConfig['new'];
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <span className={`text-sm ${config.textColor}`}>{config.label}</span>
                      <Badge className={`${config.color}/20 ${config.textColor}`}>{count}</Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-800/50 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-slate-400 text-sm">Filters:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-slate-700/50 border-white/10 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  <SelectItem value="new" className="text-white">New</SelectItem>
                  <SelectItem value="in-progress" className="text-white">In Progress</SelectItem>
                  <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                  <SelectItem value="closed" className="text-white">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-48 bg-slate-700/50 border-white/10 text-white">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="all" className="text-white">All Types</SelectItem>
                  <SelectItem value="bug-report" className="text-white">Bug Report</SelectItem>
                  <SelectItem value="feature-request" className="text-white">Feature Request</SelectItem>
                  <SelectItem value="new-subject" className="text-white">New Subject</SelectItem>
                  <SelectItem value="new-topic" className="text-white">New Topic</SelectItem>
                  <SelectItem value="more-questions" className="text-white">More Questions</SelectItem>
                  <SelectItem value="content-quality" className="text-white">Content Quality</SelectItem>
                  <SelectItem value="general" className="text-white">General</SelectItem>
                </SelectContent>
              </Select>

              <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                <SelectTrigger className="w-40 bg-slate-700/50 border-white/10 text-white">
                  <SelectValue placeholder="User Type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="all" className="text-white">All Users</SelectItem>
                  <SelectItem value="student" className="text-white">Students</SelectItem>
                  <SelectItem value="parent" className="text-white">Parents</SelectItem>
                  <SelectItem value="tutor" className="text-white">Tutors</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search feedback..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-700/50 border-white/10 text-white"
                  />
                </div>
              </div>

              <Button
                variant="ghost"
                onClick={() => {
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setUserTypeFilter('all');
                  setSearchQuery('');
                }}
                className="text-slate-400 hover:text-white"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        <Card className="bg-slate-800/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>All Feedback ({filteredFeedback.length})</span>
              <Button
                variant="ghost"
                onClick={fetchFeedback}
                className="text-slate-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-400" />
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No feedback found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFeedback.map((fb) => {
                  const statusConf = statusConfig[fb.status] || statusConfig['new'];
                  const StatusIcon = statusConf.icon;
                  
                  return (
                    <motion.div
                      key={fb.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-slate-700/30 border border-white/5 hover:border-violet-500/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedFeedback(fb)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={`${statusConf.color}/20 ${statusConf.textColor} text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConf.label}
                            </Badge>
                            <Badge className="bg-slate-600/50 text-slate-300 text-xs">
                              {feedbackTypeLabels[fb.feedbackType] || fb.feedbackType}
                            </Badge>
                            <Badge className="bg-violet-500/20 text-violet-400 text-xs capitalize">
                              {fb.userType}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-3 text-sm text-slate-400 mb-2">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {fb.name || 'Anonymous'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {fb.email || 'No email'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(fb.createdAt)}
                            </span>
                          </div>
                          
                          {fb.subject && (
                            <p className="text-white font-medium mb-1">{fb.subject}</p>
                          )}
                          <p className="text-slate-300 text-sm line-clamp-2">{fb.message}</p>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Feedback Detail Modal */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="sm:max-w-[600px] bg-slate-900 border-white/10 max-h-[90vh] overflow-y-auto">
          {selectedFeedback && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-400" />
                  Feedback Details
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Submitted {formatDate(selectedFeedback.createdAt)}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Status & Type */}
                <div className="flex items-center gap-2 flex-wrap">
                  {(() => {
                    const statusConf = statusConfig[selectedFeedback.status] || statusConfig['new'];
                    const StatusIcon = statusConf.icon;
                    return (
                      <Badge className={`${statusConf.color}/20 ${statusConf.textColor}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConf.label}
                      </Badge>
                    );
                  })()}
                  <Badge className="bg-slate-600/50 text-slate-300">
                    {feedbackTypeLabels[selectedFeedback.feedbackType] || selectedFeedback.feedbackType}
                  </Badge>
                  <Badge className="bg-violet-500/20 text-violet-400 capitalize">
                    {selectedFeedback.userType}
                  </Badge>
                </div>

                {/* User Info */}
                <Card className="bg-slate-800/50 border-white/10">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2 text-slate-300">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{selectedFeedback.name || 'Anonymous'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedFeedback.email}`} className="hover:text-violet-400">
                        {selectedFeedback.email || 'No email provided'}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Subject & Message */}
                {selectedFeedback.subject && (
                  <div>
                    <label className="text-slate-400 text-sm">Subject</label>
                    <p className="text-white font-medium">{selectedFeedback.subject}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-slate-400 text-sm">Message</label>
                  <div className="mt-1 p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-300 whitespace-pre-wrap">{selectedFeedback.message}</p>
                  </div>
                </div>

                {/* Update Status */}
                <div className="pt-4 border-t border-white/10">
                  <label className="text-slate-400 text-sm mb-2 block">Update Status</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusConfig).map(([status, config]) => {
                      const Icon = config.icon;
                      const isActive = selectedFeedback.status === status;
                      return (
                        <Button
                          key={status}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateStatus(selectedFeedback.id, status)}
                          className={isActive 
                            ? `${config.color} text-white` 
                            : `border-white/20 ${config.textColor} hover:${config.color}/20`
                          }
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFeedback;
